import { CategoryIcon, CATEGORY_META } from "@/components/ui/atoms/category-icon";
import { MarkdownText } from "@/components/ui/atoms/markdown-text";
import { SectionList } from "@/components/ui/molecules/section-list";
import type { ExerciseDetail } from "@/lib/api/api-client";

const DIFFICULTY_LABELS: Record<number, string> = {
  1: "Débutant",
  2: "Facile",
  3: "Intermédiaire",
  4: "Avancé",
  5: "Expert",
};

export interface ExercisePreSessionProps {
  exercise: ExerciseDetail;
  /** Optional program context for theming + back-link label */
  programContext?: {
    title: string;
    level: number;
    color: string;
    hue: number;
  };
  backLabel?: string;
  onBack: () => void;
  onStart: () => void;
}

export function ExercisePreSession({
  exercise,
  programContext,
  backLabel,
  onBack,
  onStart,
}: ExercisePreSessionProps) {
  const cat =
    CATEGORY_META[
      (exercise.category ?? "fondations") as keyof typeof CATEGORY_META
    ] ?? CATEGORY_META.fondations;
  const themeColor = programContext?.color ?? cat.color;
  const sections = exercise.sections ?? [];
  const skills = exercise.skills ?? [];
  const difficultyLabel = DIFFICULTY_LABELS[exercise.difficulty] ?? "";

  return (
    <div className="mx-auto max-w-[700px] px-6 pb-20 pt-8">
      {/* Back button */}
      <button
        onClick={onBack}
        className="-ml-3 mb-12 flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:bg-white/[0.04] hover:text-text-primary"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 3L5 8l5 5" />
        </svg>
        {backLabel ?? "Retour"}
      </button>

      {/* ── Hero ── */}
      <div className="mb-12 text-center">
        <div
          className="mx-auto mb-6 inline-flex size-16 items-center justify-center rounded-2xl"
          style={{
            background: `${themeColor}12`,
            boxShadow: `0 0 48px ${themeColor}15`,
            color: themeColor,
          }}
        >
          <CategoryIcon
            category={exercise.category ?? "fondations"}
            size={28}
          />
        </div>

        <span
          className="mb-4 block text-[11px] font-semibold uppercase tracking-[0.15em]"
          style={{ color: themeColor }}
        >
          {cat.label}
          {difficultyLabel && (
            <span className="text-text-muted/40">
              {" · "}
              <span className="text-text-muted">{difficultyLabel}</span>
            </span>
          )}
        </span>

        <h1 className="mb-3 text-[32px] font-bold leading-tight tracking-tight text-text-primary">
          {exercise.title}
        </h1>
        {exercise.subtitle && (
          <p className="mx-auto max-w-[50ch] text-[15px] leading-relaxed text-text-muted">
            {exercise.subtitle}
          </p>
        )}
      </div>

      {/* CTA */}
      <div className="mb-14 flex justify-center">
        <button
          onClick={onStart}
          className="group inline-flex h-12 items-center gap-3 rounded-xl px-8 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          style={{
            background: themeColor,
            boxShadow: `0 4px 28px ${themeColor}40, 0 1px 3px rgba(0,0,0,0.3)`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
            <path d="M4 2.5v9l7.5-4.5z" />
          </svg>
          Commencer
        </button>
      </div>

      <div className="mb-10 h-px bg-white/[0.06]" />

      {/* Pedagogical goal */}
      {exercise.pedagogicalGoal && (
        <section className="mb-10">
          <h2
            className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em]"
            style={{ color: themeColor }}
          >
            Objectif pédagogique
          </h2>
          <p className="text-sm italic leading-relaxed text-text-secondary">
            {exercise.pedagogicalGoal}
          </p>
        </section>
      )}

      {/* Description */}
      {exercise.description && (
        <section className="mb-10">
          <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-text-muted">
            Description
          </h2>
          <MarkdownText>{exercise.description}</MarkdownText>
        </section>
      )}

      {/* Sections */}
      {sections.length > 0 && (
        <section className="mb-10">
          <SectionList sections={sections} />
        </section>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <section>
          <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-text-muted">
            Compétences travaillées
          </h2>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <span
                key={skill}
                className="rounded-md px-3 py-1 text-xs text-text-secondary"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
